'use client';

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import { useRouter, useSearchParams } from 'next/navigation';
import { authModel } from '@/models/authModel';
import ProfileHeroCard from './components/ProfileHeroCard';
import ProfileStatsRow from './components/ProfileStatsRow';
import RatingProgressionChart from './components/RatingProgressionChart';
import NextMatchCard from './components/NextMatchCard';
import { useUpcomingMatches } from './components/useUpcomingMatches';
import ProfileSidebar from './components/ProfileSidebar';
import MatchHistoryClient from './MatchHistoryClient';
import { Tab, TabGroup } from '@/components/ui/tabs';
import type { Tables } from '@/types/supabase';
import type { PlayerProfileData } from '@/models/playerModel';
import type { MatchWithRating } from '@/components/match/types';

interface ProfilePageClientProps {
  player: Tables<'player'>;
  stats: PlayerProfileData;
  matchHistory: MatchWithRating[];
  locale: string;
  /** Server-rendered Edit Profile button; only passed for the owner. */
  editButton?: ReactNode;
}

export default function ProfilePageClient({
  player,
  stats,
  matchHistory,
  locale,
  editButton,
}: ProfilePageClientProps) {
  const tProfile = useTranslations('Profile');
  const tMatches = useTranslations('Matches');
  const search = useSearchParams();
  const router = useRouter();
  const activeTab = (search.get('tab') || 'overview').toLowerCase();
  const [isOwnProfile, setIsOwnProfile] = useState(false);
  // Own profile only — others never see a player's upcoming matches.
  const upcomingMatches = useUpcomingMatches(player, isOwnProfile);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const session = await authModel.getSession();
        if (mounted && session?.user) {
          setIsOwnProfile(session.user.id === player.user_id);
        }
      } catch {
        // ignore
      }
    })();
    return () => {
      mounted = false;
    };
  }, [player.user_id]);

  const setTab = (tab: 'overview' | 'history') => {
    const url = new URL(window.location.href);
    url.searchParams.set('tab', tab);
    router.replace(url.pathname + '?' + url.searchParams.toString());
  };

  const content = useMemo(() => {
    if (activeTab === 'history') {
      return <MatchHistoryClient player={player} matchHistory={matchHistory} />;
    }
    return (
      <>
        {/* Main + Sidebar Grid */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          {/* Main Column */}
          <div className="lg:col-span-2">
            <RatingProgressionChart matchHistory={matchHistory} isOwnProfile={isOwnProfile} />
          </div>

          {/* Sidebar */}
          <ProfileSidebar matchHistory={matchHistory} player={player} />
        </div>
      </>
    );
  }, [
    activeTab,
    player,
    matchHistory,
    isOwnProfile,
  ]);

  return (
    <div className="space-y-4 sm:space-y-6">
      <ProfileHeroCard
        player={player}
        locale={locale}
        editButton={editButton}
      />

      <NextMatchCard matches={upcomingMatches} locale={locale} />

      <ProfileStatsRow player={player} stats={stats} />

      {/* Tab Switch */}
      <div className="inline-flex overflow-hidden rounded-full border border-black/10 text-sm dark:border-white/15">
        <TabGroup>
          <Tab active={activeTab === 'overview'} onClick={() => setTab('overview')}>
            {tProfile('performanceStatistics')}
          </Tab>
          <Tab active={activeTab === 'history'} onClick={() => setTab('history')}>
            {tMatches('title')}
          </Tab>
        </TabGroup>
      </div>

      {content}
    </div>
  );
}
